import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Bell, Link2, Save, UserCog, Copy, RefreshCcw, Trash2, Eye, EyeOff, Sparkles, Check, X, Cake } from "lucide-react";
import { Textarea } from "./ui/textarea";

/**
 * Admin-only section shown inside ProfilePage. Two sub-cards:
 *  1) Push notification event toggles
 *  2) Vezice (LZS / ZGS / custom URL) floating button config
 */
const AdminSettingsSection = () => {
  const { token, user } = useAuth();
  const isAdmin = ["predsednik", "administrator"].includes(user?.vloga);

  const [pushCfg, setPushCfg] = useState(null); // {enabled: {...}, types: [...]}
  const [savingPush, setSavingPush] = useState(false);

  const [vezice, setVezice] = useState(null); // {enabled, provider, url, presets}
  const [savingVezice, setSavingVezice] = useState(false);

  const [birthday, setBirthday] = useState(null); // {enabled, message, default_message}
  const [savingBirthday, setSavingBirthday] = useState(false);

  // Sandbox test member state
  const [testMember, setTestMember] = useState(null); // {exists, user}
  const [testMemberLoading, setTestMemberLoading] = useState(false);
  const [newCredentials, setNewCredentials] = useState(null); // one-time display
  const [showPass, setShowPass] = useState(false);

  const canManageTestMember = ["predsednik", "gospodar", "administrator"].includes(user?.vloga);

  // Preglednik access requests
  const [pregRequests, setPregRequests] = useState([]);
  const [pregLoading, setPregLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin || !token) return;
    let alive = true;
    (async () => {
      const headers = { Authorization: `Bearer ${token}` };
      try {
        const [p, v, b] = await Promise.all([
          axios.get(`${API}/settings/push`, { headers }),
          axios.get(`${API}/settings/vezice`, { headers }),
          axios.get(`${API}/settings/birthday`, { headers }),
        ]);
        if (!alive) return;
        setPushCfg(p.data);
        setVezice(v.data);
        setBirthday(b.data);
      } catch (e) {
        if (alive) toast.error("Napaka pri branju nastavitev");
      }
    })();
    return () => { alive = false; };
  }, [isAdmin, token]);

  const togglePush = (key, val) => {
    setPushCfg((prev) => ({ ...prev, enabled: { ...prev.enabled, [key]: val } }));
  };

  const savePush = async () => {
    setSavingPush(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.put(`${API}/settings/push`, { enabled: pushCfg.enabled }, { headers });
      setPushCfg((prev) => ({ ...prev, enabled: res.data }));
      toast.success("Nastavitve push obvestil shranjene");
    } catch (e) {
      toast.error("Napaka pri shranjevanju");
    } finally {
      setSavingPush(false);
    }
  };

  const saveVezice = async () => {
    setSavingVezice(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        enabled: vezice.enabled,
        provider: vezice.provider,
        url: vezice.url || null,
        mode: vezice.mode || null,
      };
      const res = await axios.put(`${API}/settings/vezice`, payload, { headers });
      setVezice((prev) => ({ ...prev, ...res.data }));
      toast.success("Nastavitve vezic shranjene");
    } catch (e) {
      toast.error("Napaka pri shranjevanju");
    } finally {
      setSavingVezice(false);
    }
  };

  const saveBirthday = async () => {
    setSavingBirthday(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.put(`${API}/settings/birthday`, {
        enabled: birthday.enabled,
        message: birthday.message || null,
      }, { headers });
      setBirthday(res.data);
      toast.success("Nastavitve čestitk shranjene");
    } catch (e) {
      toast.error("Napaka pri shranjevanju");
    } finally {
      setSavingBirthday(false);
    }
  };

  // ==== Sandbox test member handlers ====
  const fetchTestMember = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/admin/test-member`, { headers });
      setTestMember(res.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (canManageTestMember && token) fetchTestMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageTestMember, token]);

  const createOrResetTestMember = async () => {
    setTestMemberLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/admin/test-member`, {}, { headers });
      setNewCredentials({
        lovska_izkaznica: res.data.lovska_izkaznica,
        stevilka_clana: res.data.stevilka_clana,
        geslo: res.data.geslo,
      });
      toast.success("Testni član ustvarjen");
      await fetchTestMember();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri ustvarjanju");
    } finally {
      setTestMemberLoading(false);
    }
  };

  const deleteTestMember = async () => {
    if (!confirm("Res želite izbrisati testnega člana?")) return;
    setTestMemberLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/admin/test-member`, { headers });
      setTestMember({ exists: false, user: null });
      setNewCredentials(null);
      toast.success("Testni član izbrisan");
    } catch (e) {
      toast.error("Napaka pri brisanju");
    } finally {
      setTestMemberLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success("Kopirano");
    } catch (_) {
      toast.error("Kopiranje ni uspelo");
    }
  };

  // ==== Preglednik access requests handlers ====
  const fetchPregRequests = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/ai-jaw/preglednik-requests`, { headers });
      setPregRequests(res.data || []);
    } catch (_) { /* silent */ }
  };

  useEffect(() => {
    if (canManageTestMember && token) fetchPregRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageTestMember, token]);

  const approvePregRequest = async (req) => {
    setPregLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(`${API}/ai-jaw/preglednik-requests/${req.id}/approve`, {}, { headers });
      toast.success(res.data?.message || "Odobreno");
      await fetchPregRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka pri odobravanju");
    } finally {
      setPregLoading(false);
    }
  };

  const rejectPregRequest = async (req) => {
    if (!confirm(`Zavrni zahtevek za ${req.user_ime} ${req.user_priimek}?`)) return;
    setPregLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/ai-jaw/preglednik-requests/${req.id}/reject`, {}, { headers });
      toast.success("Zavrnjeno");
      await fetchPregRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Napaka");
    } finally {
      setPregLoading(false);
    }
  };

  if (!isAdmin && !canManageTestMember) return null;

  return (
    <div className="space-y-4">
      {isAdmin && (
      /* Push notification settings */
      <Card data-testid="admin-push-settings-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            Nastavitve push obvestil
          </CardTitle>
          <CardDescription>
            Vklopite ali izklopite posamezne tipe obvestil. Pri vsakem tipu je v oklepaju napisano, komu se pošilja (vsem članom LD ali samo dotičnemu članu).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!pushCfg ? (
            <div className="text-sm text-muted-foreground">Nalagam...</div>
          ) : (
            <>
              {pushCfg.types.map((t) => (
                <div key={t.key} className="flex items-center justify-between gap-3 py-1">
                  <Label htmlFor={`push-${t.key}`} className="text-sm cursor-pointer">
                    {t.label}
                  </Label>
                  <Switch
                    id={`push-${t.key}`}
                    checked={!!pushCfg.enabled[t.key]}
                    onCheckedChange={(v) => togglePush(t.key, v)}
                    data-testid={`push-toggle-${t.key}`}
                  />
                </div>
              ))}
              <Button
                onClick={savePush}
                disabled={savingPush}
                size="sm"
                className="mt-2"
                data-testid="push-settings-save-btn"
              >
                <Save className="h-4 w-4 mr-1" />
                {savingPush ? "Shranjujem..." : "Shrani"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {isAdmin && (
      /* Vezice settings */
      <Card data-testid="admin-vezice-settings-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Link2 className="h-4 w-4 text-primary" />
            Plavajoči gumb Vezice
          </CardTitle>
          <CardDescription>
            Prikaži gumb za hiter dostop do vezic LZS ali ZGS znotraj aplikacije.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!vezice ? (
            <div className="text-sm text-muted-foreground">Nalagam...</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="vezice-enabled" className="text-sm cursor-pointer">
                  Prikaži plavajoči gumb Vezice
                </Label>
                <Switch
                  id="vezice-enabled"
                  checked={!!vezice.enabled}
                  onCheckedChange={(v) => setVezice((p) => ({ ...p, enabled: v }))}
                  data-testid="vezice-enabled-toggle"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Ponudnik / vir</Label>
                <RadioGroup
                  value={vezice.provider}
                  onValueChange={(v) => setVezice((p) => {
                    const preset = p.presets?.[v] || {};
                    return {
                      ...p,
                      provider: v,
                      url: v === "custom" ? (p.url || "") : (preset.url || ""),
                      mode: preset.mode || (v === "custom" ? (p.mode || "url") : "url"),
                    };
                  })}
                  className="space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="lzs" id="vez-lzs" data-testid="vezice-provider-lzs" />
                    <Label htmlFor="vez-lzs" className="text-sm cursor-pointer">
                      LZS – Lovska zveza Slovenije
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="zgs" id="vez-zgs" data-testid="vezice-provider-zgs" />
                    <Label htmlFor="vez-zgs" className="text-sm cursor-pointer">
                      ZGS – Zavod za gozdove Slovenije (QR skener)
                    </Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="custom" id="vez-custom" data-testid="vezice-provider-custom" />
                    <Label htmlFor="vez-custom" className="text-sm cursor-pointer">
                      Poljubna povezava
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">URL naslov (opcijsko pri ZGS)</Label>
                <Input
                  value={vezice.url || ""}
                  onChange={(e) => setVezice((p) => ({ ...p, url: e.target.value }))}
                  placeholder={vezice.provider === "zgs" ? "Rezervni URL (npr. ZGS portal)" : "https://..."}
                  disabled={vezice.provider === "lzs"}
                  data-testid="vezice-url-input"
                />
                {vezice.provider === "lzs" && (
                  <p className="text-[11px] text-muted-foreground">
                    URL je fiksen: {vezice.presets?.lzs?.url}
                  </p>
                )}
                {vezice.provider === "zgs" && (
                  <p className="text-[11px] text-muted-foreground">
                    ZGS pri kliku odpre skener QR kode s kamero telefona. URL je opcijski (rezervni prehod).
                  </p>
                )}
              </div>

              <Button
                onClick={saveVezice}
                disabled={savingVezice}
                size="sm"
                data-testid="vezice-settings-save-btn"
              >
                <Save className="h-4 w-4 mr-1" />
                {savingVezice ? "Shranjujem..." : "Shrani"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {isAdmin && birthday && (
      /* Rojstnodnevne čestitke */
      <Card data-testid="birthday-settings-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Cake className="h-4 w-4" />
            Rojstnodnevne čestitke
          </CardTitle>
          <CardDescription>
            Aplikacija slavljencu samodejno pošlje push obvestilo in e-mail, vsem članom pa prikaže obvestilo na nadzorni plošči.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="birthday-enabled" className="text-sm">Samodejne čestitke vklopljene</Label>
            <Switch
              id="birthday-enabled"
              checked={!!birthday.enabled}
              onCheckedChange={(v) => setBirthday((prev) => ({ ...prev, enabled: v }))}
              data-testid="birthday-enabled-switch"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">Besedilo čestitke</Label>
            <Textarea
              rows={3}
              value={birthday.message || ""}
              onChange={(e) => setBirthday((prev) => ({ ...prev, message: e.target.value }))}
              data-testid="birthday-message-input"
            />
            <p className="text-xs text-muted-foreground">
              Uporabite <span className="font-mono">{"{ime}"}</span> za ime slavljenca in <span className="font-mono">{"{ld}"}</span> za naziv lovske družine.
            </p>
          </div>
          <Button
            onClick={saveBirthday}
            disabled={savingBirthday}
            size="sm"
            data-testid="birthday-settings-save-btn"
          >
            <Save className="h-4 w-4 mr-1" />
            {savingBirthday ? "Shranjujem..." : "Shrani"}
          </Button>
        </CardContent>
      </Card>
      )}

      {canManageTestMember && pregRequests.length > 0 && (
      /* Preglednik access requests */
      <Card data-testid="preglednik-requests-card" className="border-amber-300/60 dark:border-amber-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-600" />
            Zahtevki za pravice preglednika ({pregRequests.length})
          </CardTitle>
          <CardDescription>
            Člani, ki so zaprosili za dostop do AI ocene starosti. Ob odobritvi jim v enem kliku dodelite badge <b>Preglednik divjačine</b>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {pregRequests.map((req) => (
            <div
              key={req.id}
              className="flex items-center justify-between gap-2 p-3 rounded-md bg-muted/50 border"
              data-testid={`preg-request-row-${req.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{req.user_ime} {req.user_priimek}</div>
                <div className="text-xs text-muted-foreground">
                  Izk. <span className="font-mono">{req.user_izkaznica}</span> · {new Date(req.requested_at).toLocaleString("sl-SI")}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 h-8 px-2"
                  onClick={() => rejectPregRequest(req)}
                  disabled={pregLoading}
                  data-testid={`preg-reject-${req.id}`}
                  title="Zavrni"
                >
                  <X className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3"
                  onClick={() => approvePregRequest(req)}
                  disabled={pregLoading}
                  data-testid={`preg-approve-${req.id}`}
                >
                  <Check className="h-4 w-4 mr-1" />
                  Odobri
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      )}

      {canManageTestMember && (
      /* Sandbox test member */
      <Card data-testid="admin-test-member-card" className="border-amber-300/60 dark:border-amber-800/60">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <UserCog className="h-4 w-4 text-amber-600" />
            Testni član (sandbox)
          </CardTitle>
          <CardDescription>
            Ustvarite testni račun z vlogo <b>Član</b>, da vidite aplikacijo skozi njegove oči. Ta uporabnik je <b>izključen</b> iz štetja članov, statistik, izvozov in seznamov članov. Vidijo ga samo administratorji.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!testMember ? (
            <div className="text-sm text-muted-foreground">Nalagam...</div>
          ) : testMember.exists ? (
            <div className="space-y-3">
              <div className="text-sm bg-muted/50 rounded p-3">
                <div className="font-medium">Testni račun je aktiven</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Št. lovske izkaznice: <span className="font-mono">{testMember.user.lovska_izkaznica}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Ustvarjen: {new Date(testMember.user.ustvarjeno).toLocaleString("sl-SI")}
                </div>
              </div>
              {newCredentials && (
                <div className="text-sm bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 rounded p-3 space-y-2">
                  <div className="font-medium text-amber-900 dark:text-amber-100">Poverilnice (shranite jih zdaj – geslo ne bo več prikazano)</div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="min-w-[110px] text-muted-foreground">Št. izkaznice:</span>
                    <span className="font-mono flex-1">{newCredentials.lovska_izkaznica}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => copyToClipboard(newCredentials.lovska_izkaznica)} data-testid="test-copy-izkaznica">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="min-w-[110px] text-muted-foreground">Geslo / Št. člana:</span>
                    <span className="font-mono flex-1">{showPass ? newCredentials.geslo : "••••••"}</span>
                    <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => setShowPass(!showPass)} data-testid="test-toggle-pass">
                      {showPass ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                    </Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2" onClick={() => copyToClipboard(newCredentials.geslo)} data-testid="test-copy-geslo">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="text-[11px] text-amber-800 dark:text-amber-200">
                    Odjavite se in prijavite z zgornjimi poverilnicami, da vidite aplikacijo kot navaden član.
                  </p>
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={createOrResetTestMember}
                  disabled={testMemberLoading}
                  data-testid="test-member-reset-btn"
                >
                  <RefreshCcw className="h-4 w-4 mr-1" />
                  Novo geslo
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={deleteTestMember}
                  disabled={testMemberLoading}
                  data-testid="test-member-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Izbriši
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              onClick={createOrResetTestMember}
              disabled={testMemberLoading}
              data-testid="test-member-create-btn"
            >
              <UserCog className="h-4 w-4 mr-1" />
              {testMemberLoading ? "Ustvarjam..." : "Ustvari testnega člana"}
            </Button>
          )}
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default AdminSettingsSection;
